import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { ReportTemplate } from 'dashboard/models/report-template';
import { Tag } from 'dashboard/models/tag';
import ResourceReport from 'dashboard/modules/ResourceReport';
import { ModuleResourceReportV2 } from 'dashboard/modules/ResourceReportV2';
import { useGetReportObjectByIdQuery } from 'dashboard/redux/api/account';
import { useUpdateReportTagsMutation } from 'dashboard/redux/api/report';
import useRTKErrorHandler from 'lib/hooks/useRTKErrorHandler';
import { ApiModel } from 'lib/interfaces/api';
import { FeatureFlagsMap } from 'lib/interfaces/feature-flags';

export interface ResourceReportV2Props {
  module: ModuleResourceReportV2;
  currentObjectToken: string;
  reportToken: string;
  featureFlags: FeatureFlagsMap;
}

const ResourceReportV2 = ({
  currentObjectToken,
  reportToken,
  module,
  featureFlags,
}: ResourceReportV2Props) => {
  const { data, error, isLoading, refetch } = useGetReportObjectByIdQuery({
    id: currentObjectToken,
    reportId: reportToken,
  });

  useRTKErrorHandler({
    error,
    onError: () => {
      return {
        message: 'Failed to load Report.',
        level: MessageType.Failure,
      };
    },
  });

  const [onUpdateReportTags, { error: updateReportTagsError }] = useUpdateReportTagsMutation();

  useRTKErrorHandler({
    error: updateReportTagsError,
    onError: (error) => ({
      message: (error?.errors?.at(0)?.details as string) || error.message,
      level: MessageType.Failure,
    }),
  });

  const handleUpdateReportTags = async (id: string, tags: string[]) => {
    await onUpdateReportTags({ id, tags });
    await refetch();
  };

  if (isLoading || !data) {
    return <LoadingIndicator />;
  }

  const report = data.data;
  const reportTemplate = data.included?.find(
    (object) => object.id === data.data.relationships['report-template']?.data.id,
  ) as ReportTemplate;
  const reportTags = data.included?.filter((object) => object.type === ApiModel.Tag) as Tag[];

  // Filter on report template type
  if (module['report-template-type'] && reportTemplate?.type !== module['report-template-type']) {
    return null;
  }

  return (
    <ResourceReport
      report={report}
      reportTemplate={reportTemplate}
      defaultAccordionOpen={module['default-accordion-open'] ?? true}
      featureFlags={featureFlags}
      tags={reportTags}
      handleUpdateReportTags={handleUpdateReportTags}
    />
  );
};

export default ResourceReportV2;
